import { capitalize } from 'lodash';
import { uniq } from 'lodash/fp';
import { FieldPath } from 'react-hook-form';

import { ThemeSetAttributes } from 'dashboard/models/theme-set';
import {
  StyleValueFontWeight,
  StyleValueJustify,
  StyleValueTextTransform,
} from 'lib/inquiryStyles';

export enum ThemeSetEditorFormControlType {
  BoxDimensions, // expects values of type StyleValueBoxDimensions
  Color, // expects values of type StyleValueColorConfig
  FontFamily, // expects values of type string
  Select, // expects values that are a string or enum type (like StyleValueFontWeight, StyleValueTextTransform, StyleValueJustify, etc.)
  Size, // expects values of type StyleValueSizeConfig
}

interface BaseControl {
  type: ThemeSetEditorFormControlType;
}

interface BoxDimensionsControl extends BaseControl {
  type: ThemeSetEditorFormControlType.BoxDimensions;
}

interface ColorControl extends BaseControl {
  type: ThemeSetEditorFormControlType.Color;
  // Fields to check color contrast against
  checkWCAGComplianceAgainst: FieldPath<ThemeSetAttributes>[];
}

interface FontControl extends BaseControl {
  type: ThemeSetEditorFormControlType.FontFamily;
}

interface SelectControl extends BaseControl {
  type: ThemeSetEditorFormControlType.Select;
  options: { value: string; label: string }[];
}

interface SizeControl extends BaseControl {
  type: ThemeSetEditorFormControlType.Size;
  min: number;
  max: number;
}

export type ThemeSetEditorFormControl =
  | BoxDimensionsControl
  | ColorControl
  | FontControl
  | SelectControl
  | SizeControl;

export interface ThemeSetEditorFormControlConfig {
  label: string;
  description?: string;
  /** Field name used to access the value of the control in the form */
  name: FieldPath<ThemeSetAttributes>;
  controlType: ThemeSetEditorFormControl;
}

export interface ThemeSetEditorFormCategory {
  heading: string;
  description?: string;
  path: string;
  controls: ThemeSetEditorFormControlConfig[];
  subCategories?: Pick<ThemeSetEditorFormCategory, 'heading' | 'controls' | 'description'>[];
}

const STYLE_VALUE_FONT_WEIGHT_OPTIONS = Object.values(StyleValueFontWeight).map((fontWeight) => ({
  value: fontWeight,
  label: capitalize(fontWeight),
}));

const STYLE_VALUE_JUSTIFY_OPTIONS = Object.values(StyleValueJustify).map((justify) => ({
  value: justify,
  label: capitalize(justify),
}));

const STYLE_VALUE_TEXT_TRANSFORM_OPTIONS = Object.values(StyleValueTextTransform).map(
  (textTransform) => ({
    value: textTransform,
    label: capitalize(textTransform),
  }),
);

const PRIMARY_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Primary color',
  description:
    'Brand color for buttons, links, and pictographs. More specific attribute values may override primary color value.',
  name: 'light-base-theme.color-primary',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.background-color'],
  },
};

const ERROR_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Error color',
  description: 'Text and outline color for errors messaging',
  name: 'light-base-theme.color-error',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.background-color'],
  },
};

const BACKGROUND_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Background color',
  description: 'Background color for Inquiry window',
  name: 'light-base-theme.background-color',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: [
      'light-base-theme.font-color-body',
      'light-base-theme.font-color-heading',
      'light-base-theme.font-color-small',
    ],
  },
};

const HEADING_TEXT_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Heading text color',
  name: 'light-base-theme.font-color-heading',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.background-color'],
  },
};

const BODY_TEXT_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Body text color',
  name: 'light-base-theme.font-color-body',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.background-color'],
  },
};

const SMALL_TEXT_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Small text color',
  name: 'light-base-theme.font-color-small',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.background-color'],
  },
};

const LINK_TEXT_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Link text color',
  name: 'light-base-theme.link-font-color',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.background-color'],
  },
};

const PRIMARY_BUTTON_FILL_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Primary button fill color',
  description: 'Background color for primary buttons',
  name: 'light-base-theme.button-primary-background-color',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: [
      'light-base-theme.background-color',
      'light-base-theme.button-primary-font-color',
    ],
  },
};

const PRIMARY_BUTTON_DISABLED_FILL_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Primary button disabled state fill color',
  description: 'Background color for primary buttons when in disabled state',
  name: 'light-base-theme.button-primary-background-color-disabled',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: [
      'light-base-theme.background-color',
      'light-base-theme.button-primary-font-color',
    ],
  },
};

const PRIMARY_BUTTON_TEXT_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Primary button text color',
  name: 'light-base-theme.button-primary-font-color',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: [
      'light-base-theme.button-primary-background-color',
      'light-base-theme.button-primary-background-color-disabled',
    ],
  },
};

const PRIMARY_BUTTON_BORDER_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Primary button border color',
  name: 'light-base-theme.button-primary-border-color',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.background-color'],
  },
};

const SECONDARY_BUTTON_FILL_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Secondary button fill color',
  description: 'Background color for secondary buttons',
  name: 'light-base-theme.button-secondary-background-color',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: [
      'light-base-theme.background-color',
      'light-base-theme.button-secondary-font-color',
    ],
  },
};

const SECONDARY_BUTTON_DISABLED_FILL_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Secondary button disabled state fill color',
  description: 'Background color for secondary buttons when in disabled state',
  name: 'light-base-theme.button-secondary-background-color-disabled',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: [
      'light-base-theme.background-color',
      'light-base-theme.button-secondary-font-color',
    ],
  },
};

const SECONDARY_BUTTON_TEXT_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Secondary button text color',
  name: 'light-base-theme.button-secondary-font-color',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: [
      'light-base-theme.button-secondary-background-color',
      'light-base-theme.button-secondary-background-color-disabled',
    ],
  },
};

const SECONDARY_BUTTON_BORDER_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Secondary button border color',
  name: 'light-base-theme.button-secondary-border-color',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.background-color'],
  },
};

const INPUT_BACKGROUND_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Input background color',
  name: 'light-base-theme.input-background-color',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.font-color-body'],
  },
};

const INPUT_BORDER_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Input border color',
  name: 'light-base-theme.input-border-color',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.font-color-body'],
  },
};

const ILLUSTRATION_FILL_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Illustration fill color',
  description: 'Main fill color for primary areas in key elements of illustrations',
  name: 'light-base-theme.icon-color-primary',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.background-color'],
  },
};

const ILLUSTRATION_BACKGROUND_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Illustration background color',
  description: 'Background fill color for key elements of illustrations',
  name: 'light-base-theme.icon-color-background',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.background-color'],
  },
};

const ILLUSTRATION_STROKE_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Illustration stroke color',
  description: 'Outline color for illustrations',
  name: 'light-base-theme.icon-color-stroke',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.background-color'],
  },
};

const ILLUSTRATION_HIGHLIGHT_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Illustration highlight color',
  description: 'Accent color used as background for non-key elements of illustrations',
  name: 'light-base-theme.icon-color-highlight',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.background-color'],
  },
};

const ILLUSTRATION_SUCCESS_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Illustration success color',
  description: 'Background color of the check icon that appears on success screens',
  name: 'light-inquiry-theme.icon-success-primary-color',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-base-theme.background-color'],
  },
};

const HEADER_BUTTON_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Header button color',
  description: 'Icon color for back and close buttons',
  name: 'light-inquiry-theme.navbar-icon-color',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-inquiry-theme.navbar-background-color'],
  },
};

const HEADER_BACKGROUND_COLOR_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Header background color',
  name: 'light-inquiry-theme.navbar-background-color',
  controlType: {
    type: ThemeSetEditorFormControlType.Color,
    checkWCAGComplianceAgainst: ['light-inquiry-theme.navbar-icon-color'],
  },
};

const HEADING_TEXT_FONT_FAMILY_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Heading font',
  name: 'light-base-theme.font-family-heading',
  controlType: { type: ThemeSetEditorFormControlType.FontFamily },
};

const HEADING_TEXT_FONT_SIZE_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Heading font size',
  name: 'light-base-theme.font-size-heading',
  controlType: { type: ThemeSetEditorFormControlType.Size, min: 14, max: 36 },
};

const HEADING_TEXT_FONT_WEIGHT_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Heading font weight',
  name: 'light-base-theme.font-weight-heading',
  controlType: {
    type: ThemeSetEditorFormControlType.Select,
    options: STYLE_VALUE_FONT_WEIGHT_OPTIONS,
  },
};

const HEADING_TEXT_LINE_HEIGHT_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Heading line height',
  name: 'light-base-theme.font-line-height-heading',
  controlType: { type: ThemeSetEditorFormControlType.Size, min: 14, max: 54 },
};

const HEADING_TEXT_ALIGNMENT_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Heading text alignment',
  name: 'light-base-theme.font-justify-heading',
  controlType: {
    type: ThemeSetEditorFormControlType.Select,
    options: STYLE_VALUE_JUSTIFY_OPTIONS,
  },
};

const BODY_TEXT_FONT_FAMILY_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Body font',
  name: 'light-base-theme.font-family-body',
  description: 'Font for body text and buttons',
  controlType: { type: ThemeSetEditorFormControlType.FontFamily },
};

const BODY_TEXT_FONT_SIZE_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Body font size',
  name: 'light-base-theme.font-size-body',
  controlType: { type: ThemeSetEditorFormControlType.Size, min: 12, max: 22 },
};

const BODY_TEXT_FONT_WEIGHT_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Body font weight',
  name: 'light-base-theme.font-weight-body',
  controlType: {
    type: ThemeSetEditorFormControlType.Select,
    options: STYLE_VALUE_FONT_WEIGHT_OPTIONS,
  },
};

const BODY_TEXT_LINE_HEIGHT_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Body line height',
  name: 'light-base-theme.font-line-height-body',
  controlType: { type: ThemeSetEditorFormControlType.Size, min: 12, max: 32 },
};

const BODY_TEXT_ALIGNMENT_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Body text alignment',
  name: 'light-base-theme.font-justify-body',
  controlType: {
    type: ThemeSetEditorFormControlType.Select,
    options: STYLE_VALUE_JUSTIFY_OPTIONS,
  },
};

const SMALL_TEXT_FONT_FAMILY_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Small text font',
  name: 'light-base-theme.font-family-small',
  controlType: { type: ThemeSetEditorFormControlType.FontFamily },
};

const SMALL_TEXT_FONT_SIZE_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Small text font size',
  name: 'light-base-theme.font-size-small',
  controlType: { type: ThemeSetEditorFormControlType.Size, min: 10, max: 16 },
};

const SMALL_TEXT_FONT_WEIGHT_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Small text font weight',
  name: 'light-base-theme.font-weight-small',
  controlType: {
    type: ThemeSetEditorFormControlType.Select,
    options: STYLE_VALUE_FONT_WEIGHT_OPTIONS,
  },
};

const SMALL_TEXT_LINE_HEIGHT_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Small text line height',
  name: 'light-base-theme.font-line-height-small',
  controlType: { type: ThemeSetEditorFormControlType.Size, min: 10, max: 24 },
};

const SMALL_TEXT_ALIGNMENT_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Small text alignment',
  name: 'light-base-theme.font-justify-small',
  controlType: {
    type: ThemeSetEditorFormControlType.Select,
    options: STYLE_VALUE_JUSTIFY_OPTIONS,
  },
};

const BUTTON_FONT_WEIGHT_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Button font weight',
  name: 'light-base-theme.button-font-weight',
  controlType: {
    type: ThemeSetEditorFormControlType.Select,
    options: STYLE_VALUE_FONT_WEIGHT_OPTIONS,
  },
};

const BUTTON_TEXT_TRANSFORM_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Button text transform',
  name: 'light-base-theme.button-text-transform',
  controlType: {
    type: ThemeSetEditorFormControlType.Select,
    options: STYLE_VALUE_TEXT_TRANSFORM_OPTIONS,
  },
};

const BUTTON_BORDER_RADIUS_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Button border radius',
  name: 'light-base-theme.button-border-radius',
  controlType: { type: ThemeSetEditorFormControlType.Size, min: 0, max: 30 },
};

const BUTTON_SHADOW_STRENGTH_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Button shadow style',
  name: 'light-base-theme.button-shadow-strength',
  controlType: {
    type: ThemeSetEditorFormControlType.Select,
    options: [
      { label: 'Flat', value: '0' },
      { label: 'Raised', value: '1' },
    ],
  },
};

const PRIMARY_BUTTON_BORDER_WIDTH_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Primary button border width',
  name: 'light-base-theme.button-primary-border-width',
  controlType: { type: ThemeSetEditorFormControlType.Size, min: 0, max: 4 },
};

const SECONDARY_BUTTON_BORDER_WIDTH_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Secondary button border width',
  name: 'light-base-theme.button-secondary-border-width',
  controlType: { type: ThemeSetEditorFormControlType.Size, min: 0, max: 4 },
};

const INPUT_BORDER_RADIUS_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Input border radius',
  name: 'light-base-theme.input-border-radius',
  controlType: { type: ThemeSetEditorFormControlType.Size, min: 0, max: 30 },
};

const INPUT_BORDER_WIDTH_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Input border width',
  name: 'light-base-theme.input-border-width',
  controlType: { type: ThemeSetEditorFormControlType.BoxDimensions },
};

const WINDOW_BORDER_RADIUS_CONTROL: ThemeSetEditorFormControlConfig = {
  label: 'Embedded frame radius',
  description: 'Border radius for Inquiry modals. Only used when the Inquiry is embedded.',
  name: 'light-inquiry-theme.modal-border-radius',
  controlType: { type: ThemeSetEditorFormControlType.Size, min: 0, max: 70 },
};
/**
 * Groups the controls for the theme set editor into categories based on the control's associated component
 */
export const THEME_SET_FORM_COMPONENT_CATEGORIES: ThemeSetEditorFormCategory[] = [
  {
    heading: 'All colors',
    path: 'colors',
    controls: [
      PRIMARY_COLOR_CONTROL,
      ERROR_COLOR_CONTROL,
      BACKGROUND_COLOR_CONTROL,
      HEADING_TEXT_COLOR_CONTROL,
      BODY_TEXT_COLOR_CONTROL,
      SMALL_TEXT_COLOR_CONTROL,
      LINK_TEXT_COLOR_CONTROL,
      PRIMARY_BUTTON_FILL_COLOR_CONTROL,
      PRIMARY_BUTTON_DISABLED_FILL_COLOR_CONTROL,
      PRIMARY_BUTTON_TEXT_COLOR_CONTROL,
      PRIMARY_BUTTON_BORDER_COLOR_CONTROL,
      SECONDARY_BUTTON_FILL_COLOR_CONTROL,
      SECONDARY_BUTTON_DISABLED_FILL_COLOR_CONTROL,
      SECONDARY_BUTTON_TEXT_COLOR_CONTROL,
      SECONDARY_BUTTON_BORDER_COLOR_CONTROL,
      INPUT_BACKGROUND_COLOR_CONTROL,
      INPUT_BORDER_COLOR_CONTROL,
      ILLUSTRATION_FILL_COLOR_CONTROL,
      ILLUSTRATION_BACKGROUND_COLOR_CONTROL,
      ILLUSTRATION_STROKE_COLOR_CONTROL,
      ILLUSTRATION_HIGHLIGHT_COLOR_CONTROL,
      ILLUSTRATION_SUCCESS_COLOR_CONTROL,
      HEADER_BUTTON_COLOR_CONTROL,
      HEADER_BACKGROUND_COLOR_CONTROL,
    ],
  },
  {
    heading: 'All text',
    path: 'text',
    controls: [],
    subCategories: [
      {
        heading: 'Heading text',
        description: 'Titles and section headers',
        controls: [
          HEADING_TEXT_COLOR_CONTROL,
          HEADING_TEXT_FONT_FAMILY_CONTROL,
          HEADING_TEXT_FONT_SIZE_CONTROL,
          HEADING_TEXT_FONT_WEIGHT_CONTROL,
          HEADING_TEXT_LINE_HEIGHT_CONTROL,
          HEADING_TEXT_ALIGNMENT_CONTROL,
        ],
      },
      {
        heading: 'Body text',
        description: 'Paragraphs and larger blocks of descriptive text',
        controls: [
          BODY_TEXT_COLOR_CONTROL,
          BODY_TEXT_FONT_FAMILY_CONTROL,
          BODY_TEXT_FONT_SIZE_CONTROL,
          BODY_TEXT_FONT_WEIGHT_CONTROL,
          BODY_TEXT_LINE_HEIGHT_CONTROL,
          BODY_TEXT_ALIGNMENT_CONTROL,
        ],
      },
      {
        heading: 'Small text',
        description: 'Labels, captions, and footnotes',
        controls: [
          SMALL_TEXT_COLOR_CONTROL,
          SMALL_TEXT_FONT_FAMILY_CONTROL,
          SMALL_TEXT_FONT_SIZE_CONTROL,
          SMALL_TEXT_FONT_WEIGHT_CONTROL,
          SMALL_TEXT_LINE_HEIGHT_CONTROL,
          SMALL_TEXT_ALIGNMENT_CONTROL,
        ],
      },
      {
        heading: 'Links',
        description: 'Hyperlinks within the UI',
        controls: [LINK_TEXT_COLOR_CONTROL],
      },
    ],
  },
  {
    heading: 'Buttons',
    path: 'buttons',
    controls: [
      BUTTON_FONT_WEIGHT_CONTROL,
      BUTTON_TEXT_TRANSFORM_CONTROL,
      BUTTON_BORDER_RADIUS_CONTROL,
      BUTTON_SHADOW_STRENGTH_CONTROL,
    ],
    subCategories: [
      {
        heading: 'Primary buttons',
        controls: [
          PRIMARY_BUTTON_FILL_COLOR_CONTROL,
          PRIMARY_BUTTON_DISABLED_FILL_COLOR_CONTROL,
          PRIMARY_BUTTON_TEXT_COLOR_CONTROL,
          PRIMARY_BUTTON_BORDER_COLOR_CONTROL,
          PRIMARY_BUTTON_BORDER_WIDTH_CONTROL,
        ],
      },

      {
        heading: 'Secondary buttons',
        controls: [
          SECONDARY_BUTTON_FILL_COLOR_CONTROL,
          SECONDARY_BUTTON_DISABLED_FILL_COLOR_CONTROL,
          SECONDARY_BUTTON_TEXT_COLOR_CONTROL,
          SECONDARY_BUTTON_BORDER_COLOR_CONTROL,
          SECONDARY_BUTTON_BORDER_WIDTH_CONTROL,
        ],
      },
    ],
  },
  {
    heading: 'Inputs',
    path: 'inputs',
    controls: [
      INPUT_BACKGROUND_COLOR_CONTROL,
      INPUT_BORDER_COLOR_CONTROL,
      INPUT_BORDER_RADIUS_CONTROL,
      INPUT_BORDER_WIDTH_CONTROL,
    ],
  },
  {
    heading: 'Illustrations',
    path: 'illustrations',
    controls: [
      ILLUSTRATION_FILL_COLOR_CONTROL,
      ILLUSTRATION_BACKGROUND_COLOR_CONTROL,
      ILLUSTRATION_STROKE_COLOR_CONTROL,
      ILLUSTRATION_HIGHLIGHT_COLOR_CONTROL,
      ILLUSTRATION_SUCCESS_COLOR_CONTROL,
    ],
  },
  {
    heading: 'General',
    path: 'general',
    controls: [
      PRIMARY_COLOR_CONTROL,
      ERROR_COLOR_CONTROL,
      BACKGROUND_COLOR_CONTROL,
      WINDOW_BORDER_RADIUS_CONTROL,
      HEADER_BUTTON_COLOR_CONTROL,
      HEADER_BACKGROUND_COLOR_CONTROL,
      // TODO: This attribute is not supported downstream yet so we are hiding it for now
      // https://github.com/persona-id/persona-web/pull/52773#discussion_r1850929452
      // {
      //   label: 'Header logo alignment',
      //   name: 'light-inquiry-theme.navbar-logo-display',
      //   controlType: { type: ThemeSetEditorFormControlType.Select },
      // },
    ],
  },
];

export const getAllAttributeControls = (): ThemeSetEditorFormControlConfig[] => {
  const controlsWithDup = THEME_SET_FORM_COMPONENT_CATEGORIES.flatMap((category) => {
    const subCategoriesControls = category?.subCategories
      ? category?.subCategories.flatMap((subCategory) => subCategory?.controls)
      : [];
    return [...category.controls, ...subCategoriesControls];
  }).map((control) => JSON.stringify(control));
  // deduplicates Controls when it appears in multiple places in categories
  return uniq(controlsWithDup).map((control) => JSON.parse(control));
};
